import {ObjectUtils} from './ObjectUtils';

const sortColumnSources = (a, b) => {
    // put identity values at the top
    const aIsIdentity = Boolean(a.groupingMethodId === null);
    const bIsIdentity = Boolean(b.groupingMethodId === null);

    if (aIsIdentity !== bIsIdentity) {
        return aIsIdentity ? -1 : 1;
    }

    // put shown in parent items at bottom
    const aIsShownInParent = Boolean(a.sbShowInParent !== null);
    const bIsShownInParent = Boolean(b.sbShowInParent !== null);

    if (aIsShownInParent !== bIsShownInParent) {
        return aIsShownInParent ? 1 : -1;
    }

    // put associations at bottom when searching
    const aHasParent = Boolean(a.parentColumnSourceGroupName);
    const bHasParent = Boolean(b.parentColumnSourceGroupName);

    if (aHasParent === bHasParent) {
        // maintain the order of columns that are not associations
        if (!aHasParent) return 0;
    }

    return aHasParent ? -1 : 1;
};

const convertToCheckboxNodes = (nodes) => nodes.map((node) => {
    const children = convertToCheckboxNodes(node.children ?? []);

    const displayTitle = node.shortTitle || node.title || node.columnSourceGroupName || '';
    const description = node.description ?? '';

    return {
        ...node,
        value: node.name || `group-${node.columnSourceGroupName}`,
        searchValue: `${description}|${node.shortTitle ?? ''}|${node.title ?? ''}`,
        labelParts: {
            title: displayTitle,
            description,
            showGroupIcon: node?.groupingMethodId === null,
            showFilterIcon: Boolean(node?.filters?.length),
        },
        ...(children.length ? {children} : {}),
    };
});

export class QueryToolUtils {
    static buildColumnTree(allColumns) {
        if (!allColumns || allColumns.length === 0) {
            return [];
        }

        const list = ObjectUtils.DeepClone(allColumns);
        const lookup = {};

        list.forEach(obj => {
            lookup[obj.name] = obj;
            obj.children = obj.children || [];
        });

        const treeList = [];

        list.forEach(currentNode => {
            const parentNode = lookup[currentNode.parentColumnSourceGroupName];

            currentNode.description = currentNode.name;
            if (parentNode) {
                currentNode.description = currentNode.description.replace(`${parentNode.name}.`, '');
            }

            if (!parentNode || currentNode.isTopLevel) {
                treeList.push(currentNode);
            } else if (currentNode.ColumnSource?.find?.(x => !x.sbShowInParent)) {
                const columnSourceGroups = parentNode.children.filter(x => x.parentColumnSourceGroupId !== undefined);
                const rest = parentNode.children.filter(x => x.parentColumnSourceGroupId === undefined);

                const newSourceGroups = [...columnSourceGroups, currentNode]
                    .sort((a, b) => a?.title?.localeCompare(b?.title, 'en', {numeric: true}));

                parentNode.children = [...newSourceGroups, ...rest];
            }

            currentNode.ColumnSource?.forEach((x) => {
                if (x.sbShowInParent && parentNode) {
                    parentNode.children.unshift({
                        ...x,
                        shortName: x.sbShowInParent,
                        shortTitle: x.shortTitle ?? x.sbShowInParent,
                        title: x.title ?? x.sbShowInParent,
                        description: `${parentNode.description}.${currentNode.description}.${x.shortName}`,
                        className: (x?.groupingMethodId === null) ? 'sb-key-column-source ' : '',
                    });
                } else {
                    currentNode.children.push({
                        ...x,
                        description: `${currentNode.description}.${x.shortName}`,
                        className: (x?.groupingMethodId === null) ? 'sb-key-column-source ' : '',
                    });
                }
            });
        });

        Object.values(lookup).forEach(node => node.children.sort(sortColumnSources));

        return convertToCheckboxNodes(treeList);
    }

    static filterColumnTree(nodes, filterText) {
        if (!nodes || nodes.length === 0) {
            return {filteredNodes: [], expandedByFilter: []};
        }

        if (!filterText) {
            return {filteredNodes: nodes, expandedByFilter: []};
        }

        const searchTerms = filterText.toLocaleLowerCase().split(' ').filter(Boolean);
        const expandedByFilter = [];

        const recursiveSearch = (node) => {
            const searchValue = node.searchValue?.toLocaleLowerCase?.() ?? '';
            const children = (node.children ?? [])
                .map(child => recursiveSearch({...child}))
                .filter(Boolean);

            const matches = searchTerms.every(term => searchValue.includes(term));
            const hasVisibleChildren = children.length > 0;

            if (!matches && !hasVisibleChildren) {
                return null;
            }

            if (hasVisibleChildren) {
                expandedByFilter.push(node.value);
            }

            return {
                ...node,
                ...(children.length ? {children} : {}),
            };
        };

        const filteredNodes = nodes
            .map(node => recursiveSearch({...node}))
            .filter(Boolean);

        return {
            filteredNodes,
            expandedByFilter,
        };
    }
}
